import type { AgentMessagePart, ToolStep, ToolCallSummary } from '../../types/agent-messages';
import type { ParserContext } from './types';
import { thinkingPartBuilder } from './thinkingPartBuilder';
import { codeBlockParser } from './codeBlockParser';
import { toolResultParser } from './toolResultParser';
import { actionCardParser } from './actionCardParser';

export interface ParseAgentMessageInput {
  text: string;
  toolSteps: ToolStep[];
  toolCalls: ToolCallSummary[] | null;
  fullMessage: string | null;
  turnDuration?: number;
  structuredParts?: AgentMessagePart[];
}

/**
 * Orchestrator that assembles the final AgentMessagePart[] from stream state.
 *
 * Path B (forward-compat): if `structuredParts` is provided and non-empty,
 * returns them directly -- the backend has already built the parts.
 *
 * Path A (current): runs the parser pipeline in order:
 *   1. thinkingPartBuilder  -> thinking part from toolSteps
 *   2. codeBlockParser      -> codeBlock + interleaved text parts
 *   3. toolResultParser     -> accessStatus / outputPreview / notification from tool outputs
 *   4. actionCardParser     -> actionCard from decision prompts
 *   5. leftover text        -> text part
 */
export function parseAgentMessage(input: ParseAgentMessageInput): AgentMessagePart[] {
  if (input.structuredParts && input.structuredParts.length > 0) {
    // Even with backend-provided structuredParts, ensure "Generate Samples"
    // button is present when a requirementReview part exists or when the
    // text content looks like a use-case definition.
    const parts = [...input.structuredParts];
    const hasReqReview = parts.some(p => p.type === 'requirementReview');
    const hasUsecaseText = !hasReqReview && _looksLikeUsecaseDefinition(input.fullMessage ?? input.text);

    if (hasReqReview || hasUsecaseText) {
      const existingSuggested = parts.find(p => p.type === 'suggestedActions') as
        | { type: 'suggestedActions'; actions: Array<{ label: string; action: string }> }
        | undefined;

      const hasGenerateSamples = existingSuggested?.actions?.some(
        a => /generate\s*sample/i.test(a.label) || a.action === 'generate_samples',
      );

      if (!hasGenerateSamples) {
        const sampleAction = { label: 'Generate Samples', action: 'generate_samples' };
        if (existingSuggested) {
          existingSuggested.actions.unshift(sampleAction);
        } else {
          parts.push({
            type: 'suggestedActions',
            actions: [
              { label: 'Generate Samples', action: 'generate_samples' },
              { label: 'Approve and generate code', action: 'approve_usecase' },
              { label: 'Edit the use-case', action: 'edit_usecase' },
            ],
          });
        }
      }
    }
    return parts;
  }

  const ctx: ParserContext = {
    text: input.fullMessage ?? input.text,
    toolSteps: input.toolSteps,
    toolCalls: input.toolCalls,
    fullMessage: input.fullMessage,
    turnDuration: input.turnDuration,
  };

  const allParts: AgentMessagePart[] = [];

  // 1. Thinking part (does not modify text)
  const thinkingResult = thinkingPartBuilder(ctx);
  allParts.push(...thinkingResult.parts);

  // 2. Tool result parts (does not modify text, but extracts rich data from tool outputs)
  const toolCtx = { ...ctx, text: thinkingResult.remainingText };
  const toolResult = toolResultParser(toolCtx);
  allParts.push(...toolResult.parts);

  // 3. Code block extraction (modifies text -- removes fenced blocks)
  const codeCtx = { ...ctx, text: toolResult.remainingText };
  const codeResult = codeBlockParser(codeCtx);

  // 4. Action card extraction (modifies text -- removes decision prompt)
  const actionCtx = { ...ctx, text: codeResult.remainingText };
  const actionResult = actionCardParser(actionCtx);

  if (codeResult.parts.length > 0) {
    allParts.push(...codeResult.parts);
  }

  // 5. Any leftover text becomes a text part
  const leftover = actionResult.remainingText.trim();
  if (leftover) {
    allParts.push({ type: 'text', content: leftover });
  }

  allParts.push(...actionResult.parts);

  // ── Ensure "Generate Samples" button always appears after use-case definition ──
  // The backend agent sometimes omits the button from its text response,
  // so we inject it deterministically on the frontend when a requirementReview
  // part is present OR when the text looks like a use-case definition.
  const hasRequirementReview = allParts.some(p => p.type === 'requirementReview');
  const hasUsecaseTextPathA = !hasRequirementReview && _looksLikeUsecaseDefinition(ctx.text);

  if (hasRequirementReview || hasUsecaseTextPathA) {
    const existingSuggested = allParts.find(p => p.type === 'suggestedActions') as
      | { type: 'suggestedActions'; actions: Array<{ label: string; action: string }> }
      | undefined;

    const hasGenerateSamples = existingSuggested?.actions?.some(
      a => /generate\s*sample/i.test(a.label) || a.action === 'generate_samples',
    );

    if (!hasGenerateSamples) {
      const sampleAction = { label: 'Generate Samples', action: 'generate_samples' };
      if (existingSuggested) {
        existingSuggested.actions.push(sampleAction);
      } else {
        allParts.push({
          type: 'suggestedActions',
          actions: [
            { label: 'Generate Samples', action: 'generate_samples' },
            { label: 'Edit the use-case', action: 'edit_usecase' },
          ],
        });
      }
    }
  }

  return allParts;
}

/**
 * Heuristic: detect if the agent wrote a use-case definition inline as text
 * without calling the build_usecase_definition tool.
 * Requires at least 3 structural markers to avoid false positives.
 */
function _looksLikeUsecaseDefinition(text: string): boolean {
  if (!text) return false;
  const t = text.toLowerCase();

  const markers = [
    /use[- ]?case definition|report name:/i.test(text),
    t.includes('objective:'),
    /\btables?:/i.test(text) || t.includes('data sources:'),
    /\blogic(?:\s+steps)?:/i.test(text) || t.includes('business logic:') || t.includes('join strategy:'),
    /\boutput(?:\s+columns)?:/i.test(text) || t.includes('output fields:'),
    t.includes('missing information') || t.includes('need your input'),
    t.includes('grouping & aggregation') || t.includes('grouping and aggregation') || t.includes('group by:'),
  ];

  return markers.filter(Boolean).length >= 3;
}
